/// <reference types="vite-plugin-pwa/client" />
// Service-worker registration with update handling. The reload-on-activate
// behavior of registerType "autoUpdate" lives in this client module — without
// it the SW still ships skipWaiting + cleanupOutdatedCaches, which purges the
// previous deploy's hashed chunks while a stale shell is on screen, so every
// lazy route 404s after a deploy until a manual reload.
//
// onNeedReload below (#623) REPLACES that unprompted reload with a dismissible
// prompt — the cohort/session notes editors (components/cohort-notes.tsx,
// session-notes.tsx) hold unsaved textarea input, so a deploy landing mid-edit
// used to discard whatever a PL was typing with no warning at all.
import { registerSW } from "virtual:pwa-register";
import { toast } from "sonner";
import { installChunkRecovery } from "@shared/pwa/chunk-recovery";

// How long a dismissed persistent toast (below, and the chunk-recovery
// give-up toast further down) stays away before it re-shows itself. Neither
// toast's underlying condition clears on dismiss — see the two comments
// below for why each still needs to come back — so "gone until the next
// update/reload" is not an option; 10 minutes is long enough not to nag a
// session that's mid-edit, short enough that the reminder returns within one
// sitting. Mirrors apps/ops/src/lib/pwa.ts (#564) — kept as a duplicated
// constant rather than a shared one because the ops half already shipped
// under #626 and re-touching it is out of scope for #623.
const DISMISSED_TOAST_REARM_DELAY_MS = 10 * 60 * 1000;

// Holds the pending re-arm timer for the update-ready toast. Module-scoped,
// not local to onNeedReload's callback, because onNeedReload itself fires on
// EVERY SW activation — the hourly registration.update() poll above, plus one
// activation per deploy — not just once per page load. showUpdateToast used
// to be declared inside onNeedReload, so each firing created its own closure
// and its own onDismiss re-arm chain; a run of deploys (or the hourly poll
// ticking over) stacked independent 10-minute timers that each kept
// re-showing the toast, so it could resurface far more often than once per
// 10 minutes. Clearing the previous timer before scheduling a new one keeps
// exactly one re-arm outstanding regardless of how many times onNeedReload
// has fired.
let updateRearmTimer: ReturnType<typeof setTimeout> | undefined;

function showUpdateToast() {
  // A fresh SW activation (another hourly poll tick, or another deploy)
  // can call this again while an earlier dismiss's re-arm timer is still
  // pending — without clearing it here, that old timer still fires on its
  // own original schedule and re-shows the toast sooner than the 10 minutes
  // this (re)show is meant to buy.
  clearTimeout(updateRearmTimer);
  toast.info("A new version of Learn is ready", {
    id: "sw-update",
    duration: Infinity,
    position: "top-center",
    closeButton: true,
    description:
      "Save anything you're working on first — reloading discards unsaved changes.",
    onDismiss: () => {
      clearTimeout(updateRearmTimer);
      updateRearmTimer = setTimeout(
        showUpdateToast,
        DISMISSED_TOAST_REARM_DELAY_MS,
      );
    },
    action: {
      label: "Reload",
      onClick: () => window.location.reload(),
    },
  });
}

registerSW({
  immediate: true,
  // Installed PWA windows can stay open for days without a navigation that
  // would trigger an update check, so poll for new deploys hourly.
  onRegisteredSW: (_url, registration) => {
    if (registration) {
      setInterval(() => void registration.update(), 60 * 60 * 1000);
    }
  },
  // Providing onNeedReload REPLACES autoUpdate's unprompted
  // window.location.reload(). Surface a persistent toast instead and let the
  // PL pick the moment. Staying on registerType "autoUpdate" keeps the SW's
  // skipWaiting + cleanupOutdatedCaches recovery path (see file header)
  // intact.
  //
  // top-center + closeButton (#623, mirroring ops #564): sonner's
  // bottom-right default has no fixed footer to collide with here — the
  // notes editors' Save buttons sit inline in the page, not in a sticky
  // dialog footer — but top-center is still the shared pattern across both
  // apps, and it keeps this toast off the page content a PL is actively
  // reading rather than stacking on top of whatever they scrolled to.
  //
  // Why it has to re-arm on dismiss, not just fire once: by the time this
  // callback runs, the NEW service worker has already activated and
  // `cleanupOutdatedCaches` has already purged the OLD deploy's hashed
  // chunks — `onNeedReload` fires from the register's `activated` listener,
  // not from anything reversible. Dismissing the toast changes nothing about
  // that; it is not "check back later," it is "the old chunks are already
  // gone." The hourly `registration.update()` above only re-fires this
  // callback on a byte-different sw.js, so a dismiss-and-forget toast would
  // never come back, and the next lazy-route navigation would hit
  // `vite:preloadError` with no warning at all — chunk-recovery below would
  // reload out from under the PL instead. `onDismiss` (not `onAutoClose`,
  // since duration is Infinity and this only ever closes by the button or a
  // swipe) re-arms a plain timer to show it again.
  onNeedReload: showUpdateToast,
});

// If a purged chunk still slips through (deploy racing an open tab), recover
// instead of stranding the user on an error boundary — but the recovery is
// BOUNDED, and the bound is not optional.
//
// Every deploy rewrites the hashed chunk filenames, so a tab that was open
// before the deploy is still holding the OLD index.html and its next lazy-route
// navigation asks for a chunk name that no longer exists; Vite then fires
// `vite:preloadError`. That is the normal case after a merge, not an edge case.
//
// This handler used to be `() => window.location.reload()`. When the reload is
// served the same stale shell — the service worker replaying its cached
// index.html, or a stale CDN edge — the fresh page requests the same missing
// chunk, fires the same event, and reloads again: an infinite reload loop (hit
// live on the ops app, same code). Keep the counter.
//
// Ladder (see shared/pwa/chunk-recovery.ts): reload once → purge the service
// worker + Cache Storage, force-revalidate the precached assets, reload once
// more → stop, run that same cleanup once, and show the toast below. Attempts
// live in sessionStorage, so they die with the tab.
installChunkRecovery({
  onGiveUp: () => {
    // A hard-reload keyboard shortcut is not an instruction a learner on a phone
    // can follow, and learn is a phone-first app. By the time this fires the
    // ladder has purged the caches and force-revalidated the precached assets,
    // so offer the reload as a button instead of describing a chord.
    //
    // top-center + closeButton (#623, mirroring ops #564): same reasoning as
    // onNeedReload above — a persistent duration: Infinity toast otherwise
    // sits at sonner's bottom-right default indefinitely, and closeButton
    // lets it be dismissed without being forced through Reload.
    const showGiveUpToast = () => {
      toast.error("This page couldn't load", {
        id: "chunk-recovery-failed",
        duration: Infinity,
        position: "top-center",
        closeButton: true,
        description:
          "A new version was released while this tab was open. The old files have been cleared — reload to pick up the new ones.",
        // Re-arm on dismiss (#623), same reasoning as onNeedReload above but
        // more acute here: this toast is the ONLY thing saying the page is
        // broken. By the time it fires, the recovery ladder has already
        // exhausted its reloads and run its one-shot purge + repair (see the
        // ladder comment above) — dismissing it does not un-break the tab,
        // and there is no further automatic retry to fall back on.
        //
        // A single setTimeout here (not a cleared/tracked handle like the
        // update toast's updateRearmTimer above) is safe: onGiveUp fires at
        // most once per page load — chunk-recovery's own `handled` flag
        // (shared/pwa/chunk-recovery.ts) guards it — so there is only ever
        // one re-arm chain, never several stacking on top of each other.
        onDismiss: () => {
          setTimeout(showGiveUpToast, DISMISSED_TOAST_REARM_DELAY_MS);
        },
        action: {
          label: "Reload",
          onClick: () => window.location.reload(),
        },
      });
    };
    showGiveUpToast();
  },
});
